import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";
import { TIPOS_GRUPO } from '../components/actas/datosGrupo';

const MODULO = "Actas";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const formatearFecha = (fechaStr) => {
  if (!fechaStr) return "—";

  const fecha = new Date(fechaStr);

  if (Number.isNaN(fecha.getTime())) return "—";

  return fecha.toLocaleDateString("es-CO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC",
  });
};

const ListarActasPorGrupo = ({ refresh }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [actas, setActas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloActas = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloActas = useMemo(() => getModuloActas(), []);

  const puedeCrear =
    moduloActas?.crear === true ||
    moduloActas?.puedeCrear === true ||
    moduloActas?.create === true;

  const puedeActualizar =
    moduloActas?.actualizar === true ||
    moduloActas?.editar === true ||
    moduloActas?.puedeActualizar === true ||
    moduloActas?.update === true;

  const puedeEliminar =
    moduloActas?.eliminar === true ||
    moduloActas?.puedeEliminar === true ||
    moduloActas?.delete === true;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login");
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(
        id ? `/api/grupos/actas/listidgrupo/${id}` : '/api/grupos/actas/listall',
        authConfig()
      );


      const lista = Array.isArray(data?.actas)
        ? data.actas
        : Array.isArray(data)
        ? data
        : [];


      setActas([...lista].reverse());
    } catch (error) {
      console.error("Error al cargar las actas:", error);

      if (manejarErrorSesion(error)) return;

      setActas([]);

      Swal.fire(
        "Error",
        error.response?.data?.mensaje ||
          "No se pudieron cargar las actas registradas.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [id, manejarErrorSesion]);

  const generarYDescargarActa = useCallback(
    async (idActa, fichaGrupo, numeroActa) => {
      try {
        setDownloadingId(idActa);

        const response = await axios.get(
          `/api/actas/generar-docx/${idActa}`,
          {
            ...authConfig(),
            responseType: "blob",
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");

        link.href = url;

        link.setAttribute(
          "download",
          `Acta_${fichaGrupo || "grupo"}_${
            numeroActa || "sin-numero"
          }.docx`
        );

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error al generar el acta:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire("Error", "No se pudo generar el acta.", "error");
      } finally {
        setDownloadingId(null);
      }
    },
    [manejarErrorSesion]
  );

  const deleteActa = useCallback(
    async (idActa) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Acceso denegado",
          "No tienes permisos para eliminar actas.",
          "warning"
        );

        return;
      }

      try {
        const confirm = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡Esta acción no se puede deshacer!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!confirm.isConfirmed) return;

        setDeletingId(idActa);

        const { data } = await axios.delete(
          `/api/grupos/actas/delete/${idActa}`,
          authConfig()
        );

        setActas((prev) => prev.filter((acta) => acta._id !== idActa));

        Swal.fire({
          icon: "success",
          title: data?.message || "Acta eliminada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar el acta:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire("Error", "No se pudo eliminar el acta.", "error");
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  const dataTabla = useMemo(
    () =>
      actas.filter(acta => !acta.tipoActa || TIPOS_GRUPO.includes(acta.tipoActa)).map((acta) => ({
        ...acta,
        ambitoTexto: acta.ambito === 'INSTITUCIONAL' ? 'Institucional' : 'Formación',
        plantillaTexto: acta.tipoActa ? 'Plantilla específica' : 'Formato general anterior',
        fechaFormateada: formatearFecha(acta.fecha),

        funciones: (
          <div className="d-flex gap-2 flex-nowrap justify-content-end align-items-center">
            <button
              className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34 }}
              type="button"
              title="Descargar acta"
              disabled={
                loading ||
                deletingId === acta._id ||
                downloadingId === acta._id
              }
              onClick={() =>
                generarYDescargarActa(
                  acta._id,
                  acta.fichaGrupo,
                  acta.numeroActa
                )
              }
            >
              {downloadingId === acta._id ? (
                <i className="fa-solid fa-spinner fa-spin text-white"></i>
              ) : (
                <i className="fa-solid fa-file-word text-white"></i>
              )}
            </button>

            {puedeActualizar && (
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                type="button"
                title={acta.tipoActa ? 'Editar acta' : 'Editar o aplicar plantilla'}
                disabled={
                  loading ||
                  deletingId === acta._id ||
                  downloadingId === acta._id
                }
                onClick={() =>
                  navigate(`/institucional/actas/editar/${acta._id}`)
                }
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>
            )}

            {puedeEliminar && (
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                type="button"
                title="Eliminar acta"
                disabled={
                  loading ||
                  deletingId === acta._id ||
                  downloadingId === acta._id
                }
                onClick={() => deleteActa(acta._id)}
              >
                {deletingId === acta._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
              </button>
            )}
          </div>
        ),
      })),
    [
      actas,
      loading,
      deletingId,
      downloadingId,
      puedeActualizar,
      puedeEliminar,
      navigate,
      deleteActa,
      generarYDescargarActa,
    ]
  );

  const columns = useMemo(
    () => [
      { Header: "N° Acta", accessor: "numeroActa" },
      { Header: "Comité o Reunión", accessor: "nombreComiteReunion" },
      { Header: "Formato", accessor: "plantillaTexto" },
      { Header: "Institución", accessor: "institucion" },
      { Header: "Ficha", accessor: "fichaGrupo" },
      { Header: "Aprendiz", accessor: "aprendizNombre" },
      { Header: "Ciudad", accessor: "ciudad" },
      { Header: "Fecha", accessor: "fechaFormateada" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Gestión de Actas</h5>

                <small className="text-secondary">
                  Administración, descarga, edición y eliminación de actas.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                 className="btn btn-outline-dark"
                  type="button"
                  disabled={loading}
                  onClick={() => navigate(-1)}
                >
                 <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  disabled={loading}
                  onClick={getData}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={() =>
                      navigate(id ? `/institucional/actas/nuevo/${id}` : '/institucional/grupos')
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            <div className="d-flex gap-2 mb-3 align-items-center">
              <span className="text-secondary">{id ? `Actas de formación — Ficha ${id}` : 'Actas de formación de los grupos'}</span>
              {id && <button className="btn btn-outline-dark" type="button" onClick={() => navigate('/institucional/actas')}>Ver todas las actas</button>}
            </div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando actas...</div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataTabla} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">📄</div>

                <div className="fw-semibold">No hay actas registradas</div>

                <div className="small mb-3">
                  {id ? `No se encontraron actas para la ficha ${id} con este filtro.` : 'No se encontraron actas con este filtro.'}
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(id ? `/institucional/actas/nuevo/${id}` : '/institucional/grupos')
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear acta
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarActasPorGrupo;
